import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Task, FullProject, LogEntry, ProjectMetadata } from '../../types';
import { formatDate, parseDate, todayStr } from '../../utils/dateUtils';
import {
  globalCommandManager,
  CommandManagerState,
  getNextActionTimestamp,
} from '../../utils/commandManager';
import {
  ProjectSyncTracker,
  applyProjectSnapshotTransition,
} from './sync/projectSyncStateTracker';
import { functions } from '../../firebase';

export interface UseProjectHistoryAndAiActionsParams {
  currentProjectId: string;
  projectName: string;
  projectMetadata: ProjectMetadata;
  allProjectsRef: React.MutableRefObject<FullProject[]>;
  syncTracker?: ProjectSyncTracker;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  setProjectName: React.Dispatch<React.SetStateAction<string>>;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  setChangelog: React.Dispatch<React.SetStateAction<LogEntry[]>>;
  markProjectDirty: (id: string) => void;
  markTaskDirty?: (taskId: string | number, projectId?: string) => void;
  syncProjectToFirestore: (project: FullProject, force?: boolean) => Promise<unknown> | unknown;
  getProjectTasksWithCommVorgaenge?: (proj: FullProject, projectsList: FullProject[]) => Task[];
}

export function useProjectHistoryAndAiActions({
  currentProjectId,
  allProjectsRef,
  syncTracker,
  setAllProjects,
  setTasks,
  setProjectName,
  setProjectMetadata,
  setChangelog,
  markProjectDirty,
  markTaskDirty,
  syncProjectToFirestore,
  getProjectTasksWithCommVorgaenge,
}: Omit<UseProjectHistoryAndAiActionsParams, 'projectName' | 'projectMetadata'>) {
  const addLogEntry = useCallback(
    (action: LogEntry['action'], description: string, details?: string) => {
      const entry: LogEntry = {
        id: Date.now().toString() + Math.random().toString(36).substring(2, 9),
        timestamp: new Date().toISOString(),
        action,
        description,
        details,
      };
      setChangelog((prev) => [entry, ...prev].slice(0, 100));
    },
    [setChangelog]
  );

  const [commandState, setCommandState] = useState<CommandManagerState>(() =>
    globalCommandManager.getState()
  );
  const [history, setHistoryState] = useState<{ past: FullProject[][]; future: FullProject[][] }>({
    past: [],
    future: [],
  });
  const historyRef = useRef<{ past: FullProject[][]; future: FullProject[][] }>(history);
  const snapshotTimestampsRef = useRef<WeakMap<FullProject[], number>>(new WeakMap());
  const prevCommandUndoCountRef = useRef<number>(commandState.undoCount);

  const setHistory = useCallback<
    React.Dispatch<React.SetStateAction<{ past: FullProject[][]; future: FullProject[][] }>>
  >((updater) => {
    setHistoryState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      historyRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    return globalCommandManager.subscribe((state) => {
      setCommandState(state);
      // When a new command is executed (not redone), clear any stale snapshot redo stack
      if (
        state.undoCount > prevCommandUndoCountRef.current &&
        state.redoCount === 0 &&
        !globalCommandManager.isCurrentlyRedoing &&
        historyRef.current.future.length > 0
      ) {
        setHistory((h) => (h.future.length === 0 ? h : { ...h, future: [] }));
      }
      prevCommandUndoCountRef.current = state.undoCount;
    });
  }, [setHistory]);

  const saveSnapshot = useCallback(() => {
    const snapshot = [...allProjectsRef.current];
    snapshotTimestampsRef.current.set(snapshot, getNextActionTimestamp());
    globalCommandManager.clearRedo();
    const nextHistory = {
      past: [...historyRef.current.past.slice(-49), snapshot],
      future: [],
    };
    historyRef.current = nextHistory;
    setHistoryState(nextHistory);
  }, [allProjectsRef]);

  const applySnapshotTransition = useCallback(
    (currentState: FullProject[], targetState: FullProject[]) => {
      const nextTargetState = applyProjectSnapshotTransition({
        currentState,
        targetState,
        syncTracker,
        allProjectsRef,
        setAllProjects,
        markProjectDirty,
        markTaskDirty,
      });

      const currentInTarget = nextTargetState.find((p) => p.id === currentProjectId);
      if (currentInTarget) {
        const enrichedTasks = getProjectTasksWithCommVorgaenge
          ? getProjectTasksWithCommVorgaenge(currentInTarget, nextTargetState)
          : currentInTarget.tasks || [];
        setTasks(enrichedTasks);
        setProjectMetadata(currentInTarget.metadata || {});
        setProjectName(currentInTarget.name || '');
      }

      return nextTargetState;
    },
    [
      allProjectsRef,
      currentProjectId,
      getProjectTasksWithCommVorgaenge,
      markProjectDirty,
      markTaskDirty,
      setAllProjects,
      setProjectMetadata,
      setProjectName,
      setTasks,
      syncTracker,
    ]
  );

  const undo = useCallback(async () => {
    const cmdTs = globalCommandManager.canUndo ? (globalCommandManager.lastUndoTimestamp ?? 0) : -1;
    const past = historyRef.current.past;
    const topPast = past.length > 0 ? past[past.length - 1] : undefined;
    const snapTs = topPast ? (snapshotTimestampsRef.current.get(topPast) ?? 0) : -1;

    if (cmdTs < 0 && !topPast) {
      return;
    }

    if (cmdTs > snapTs && globalCommandManager.canUndo) {
      try {
        const undone = await globalCommandManager.undo();
        if (undone) {
          addLogEntry('update', `Rückgängig: ${undone.name}`);
          return;
        }
      } catch (err) {
        console.error('[Undo] Error during transactional undo:', err);
        return;
      }
    }

    if (!topPast) return;

    const currentState = [...allProjectsRef.current];
    snapshotTimestampsRef.current.set(
      currentState,
      snapTs > 0 ? snapTs : getNextActionTimestamp()
    );
    applySnapshotTransition(currentState, topPast);

    const nextHistory = {
      past: past.slice(0, -1),
      future: [currentState, ...historyRef.current.future].slice(0, 50),
    };
    historyRef.current = nextHistory;
    setHistoryState(nextHistory);
  }, [allProjectsRef, addLogEntry, applySnapshotTransition]);

  const redo = useCallback(async () => {
    const cmdRedoTs = globalCommandManager.canRedo
      ? (globalCommandManager.nextRedoTimestamp ?? 0)
      : -1;
    const future = historyRef.current.future;
    const topFuture = future.length > 0 ? future[0] : undefined;
    const snapRedoTs = topFuture ? (snapshotTimestampsRef.current.get(topFuture) ?? 0) : -1;

    if (cmdRedoTs < 0 && !topFuture) {
      return;
    }

    // The most recently undone action has the smaller original action timestamp
    if (cmdRedoTs >= 0 && (!topFuture || (snapRedoTs >= 0 && cmdRedoTs <= snapRedoTs))) {
      try {
        const redone = await globalCommandManager.redo();
        if (redone) {
          addLogEntry('update', `Wiederholen: ${redone.name}`);
          return;
        }
      } catch (err) {
        console.error('[Redo] Error during transactional redo:', err);
        return;
      }
    }

    if (!topFuture) return;

    const currentState = [...allProjectsRef.current];
    snapshotTimestampsRef.current.set(
      currentState,
      snapRedoTs > 0 ? snapRedoTs : getNextActionTimestamp()
    );
    applySnapshotTransition(currentState, topFuture);

    const nextHistory = {
      past: [...historyRef.current.past, currentState].slice(-50),
      future: future.slice(1),
    };
    historyRef.current = nextHistory;
    setHistoryState(nextHistory);
  }, [allProjectsRef, addLogEntry, applySnapshotTransition]);

  const importFromJSON = useCallback(
    async (file: File) => {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const result = event.target?.result as string;
          const parsed = JSON.parse(result);

          const projectsToImport = parsed.allProjects || parsed.projects;

          if (Array.isArray(projectsToImport)) {
            if (
              !window.confirm(
                `${projectsToImport.length} Projekte im Backup gefunden. Sollen diese importiert bzw. aktualisiert werden?`
              )
            ) {
              return;
            }

            const normalizedProjects: FullProject[] = (projectsToImport as FullProject[]).map((p) => ({
              ...p,
              tasks: (p.tasks || []).map((t: Task) => ({
                ...t,
                start: t.start ? formatDate(parseDate(t.start)) : todayStr,
                end: t.end ? formatDate(parseDate(t.end)) : todayStr,
              })),
            }));

            setAllProjects((prev) => {
              const next = [...prev];
              for (const p of normalizedProjects) {
                const idx = next.findIndex((existing) => existing.id === p.id);
                if (idx !== -1) {
                  next[idx] = p;
                } else {
                  next.push(p);
                }
                syncProjectToFirestore(p, true);
              }
              return next;
            });

            addLogEntry(
              'import',
              `${normalizedProjects.length} Projekte aus JSON-Backup importiert/aktualisiert`
            );
            alert(`${normalizedProjects.length} Projekte wurden erfolgreich importiert.`);
          } else {
            alert('Ungültiges Dateiformat.');
          }
        } catch (error) {
          console.error('Import error:', error);
          alert('Fehler beim Importieren der Datei.');
        }
      };
      reader.readAsText(file);
    },
    [syncProjectToFirestore, addLogEntry, setAllProjects]
  );

  return {
    addLogEntry,
    commandState,
    history,
    setHistory,
    saveSnapshot,
    undo,
    redo,
    importFromJSON,
  };
}

export {
  useProjectAiGenerators,
  type UseProjectAiGeneratorsParams,
} from './useProjectAiGenerators';
